import { useLabour } from '@/api/labour';
import { useSetupDrafts } from '@/api/onboarding';
import { usePayrollTypes, useRateRules } from '@/api/payroll';
import { useUsers } from '@/api/people';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
/**
 * Lønn og integrasjoner at a glance (#267, V6): the file, the employee numbers, the direct link, the rates
 * and the history, each a line with where it is set. The forms below it are unchanged.
 */
import { systemOf } from '@/pages/lonn/payrollSystem';
import { ratesOf } from '@/rules/labour';
import { useTenant } from '@/shell/tenant';
import { fullName, num } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { Link } from 'react-router-dom';
import { usePowerOffice } from './PowerOffice';
import { useTripletex } from './Tripletex';

const NAME = { tripletex: 'Tripletex', poweroffice: 'PowerOffice Go', visma: 'Visma' } as const;

function Line({
  title,
  children,
  to,
  action,
}: { title: string; children: React.ReactNode; to: string; action: string }) {
  const inPage = to.startsWith('#');
  return (
    <div className="lsetl">
      <h3>{title}</h3>
      <p>{children}</p>
      {inPage ? (
        <a className="mlink" href={to}>
          {action}
          <Icon name="chevronRight" />
        </a>
      ) : (
        <Link className="mlink" to={to}>
          {action}
          <Icon name="chevronRight" />
        </Link>
      )}
    </div>
  );
}

export function SettingsOverview() {
  const { tenant } = useTenant();
  const can = useCan();
  const sys = systemOf(tenant);
  const usersQ = useUsers();
  const tx = useTripletex(sys === 'tripletex');
  const po = usePowerOffice(sys === 'poweroffice');
  const typesQ = usePayrollTypes();
  const rulesQ = useRateRules();
  const labour = useLabour();
  const rates = ratesOf(labour.rates);
  const people = (usersQ.data ?? []).filter((u) => u.active_status);
  const missing = people.filter((u) => !u.employee_id);
  const link = sys === 'tripletex' ? tx.data : sys === 'poweroffice' ? po.data : null;
  const linkName = sys === 'tripletex' ? tx.data?.company_name : po.data?.client_name;
  const draft = useSetupDrafts(tenant?.me?.is_admin === true).data?.find((d) => d.setup === 'lonnskobling');
  return (
    <section className="lset" aria-labelledby="lset-h" data-testid="settings-overview">
      {/* the group above names the place; the overview says only what it is (#272 SS-13) */}
      <p className="lsets" id="lset-h">
        {T('Lønnsfil, ansattnumre og kobling er hver sin del av oppsettet.')}
      </p>
      <Line title={T('Lønnsfil')} to="/lonn?vis=eksport" action={T('Se lønnsgrunnlag')}>
        {sys === 'visma'
          ? T('Visma-fil støttes ikke ennå. Tripletex-filen lastes ned under Rapporter.')
          : sys
            ? T('{name}-fil, lastes ned under Rapporter og importeres i lønnssystemet.', { name: NAME[sys] })
            : T('Ikke valgt lønnssystem. CSV, Excel og PDF lastes ned under Rapporter.')}
      </Line>
      {/* one missing number opens that person, several exactly the people without one (#267 SET-04) */}
      <Line
        title={T('Ansattnummer til lønn')}
        to={
          missing.length === 1
            ? `/ansatte/${missing[0]?.id}`
            : missing.length
              ? '/ansatte?oppgave=ansattnummer'
              : '/ansatte'
        }
        action={
          missing.length === 1
            ? T('Fullfør nummer')
            : missing.length
              ? T('Se ansatte uten nummer')
              : T('Se ansatte')
        }
      >
        {usersQ.isError ? (
          T('De ansatte kunne ikke leses.')
        ) : usersQ.isPending ? (
          '–'
        ) : missing.length ? (
          <span className="lwarn">
            {T('{a} av {b} aktive ansatte har ansattnummer.', {
              a: people.length - missing.length,
              b: people.length,
            })}{' '}
            {missing.length === 1 ? T('Mangler: {name}.', { name: fullName(missing[0]) }) : ''}
          </span>
        ) : (
          T('Alle {b} aktive ansatte har ansattnummer.', { b: people.length })
        )}
      </Line>
      {/* people from a file are their own task, never a side effect of the payroll link (#267 SET-06) */}
      {can('ansatte.endre') && (
        <Line title={T('Hent ansatte')} to="/ansatte/importer" action={T('Hent fra fil')}>
          {T(
            'Excel eller CSV til Timefly. Du ser og velger de ansatte før de legges inn, og ingen blir invitert.',
          )}
        </Line>
      )}
      {sys && sys !== 'visma' && (
        <Line
          title={T('Direkte integrasjon')}
          to={draft?.status === 'draft' || draft?.status === 'paused' ? '#koblingsutkast' : '#integrasjon'}
          action={
            draft?.status === 'draft' || draft?.status === 'paused'
              ? T('Fortsett utkastet')
              : T('Se tilkoblingen')
          }
        >
          {!link
            ? '–'
            : !link.available
              ? T('Ikke slått på for bedriften ennå. Bruk filen under Rapporter.')
              : link.connected
                ? T('Koblet til {name}.', { name: linkName ?? NAME[sys] })
                : draft?.status === 'draft' || draft?.status === 'paused'
                  ? // #297 ONB-23: a started draft is said, never counted as connected
                    T('Ikke koblet til {name}. Utkastet ditt er {state}.', {
                      name: NAME[sys],
                      state: draft.status === 'paused' ? T('satt på pause') : T('lagret'),
                    })
                  : T('Ikke koblet til {name}.', { name: NAME[sys] })}
        </Line>
      )}
      <Line title={T('Satser og beregning')} to="#satser" action={T('Se satsene')}>
        {/* the real figures, not a description of them (#272) */}
        {typesQ.isPending || rulesQ.isPending || labour.pending
          ? '–'
          : T(
              '{t} lønnsarter med {r} satsregler. Arbeidsgiveravgift {a} %, feriepenger {h} %, pensjon {p} %.',
              {
                t: (typesQ.data ?? []).length,
                r: (rulesQ.data ?? []).length,
                a: num(rates.agaPct),
                h: num(rates.holidayPct),
                p: num(rates.pensionPct),
              },
            )}
      </Line>
      <Line title={T('Eksport og historikk')} to="/lonn?vis=historikk" action={T('Åpne historikk')}>
        {T('Hver fil som lastes ned eller sendes, med hvem og når.')}
      </Line>
    </section>
  );
}
